import FeatherIcon from "feather-icons-react";
import { Checkbox, FormGroup, List, ListItem, Icon } from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import { formatRut } from "src/utils/formatter";
import "./ContactDetail.scss";
import { ComunasListSemantic } from "src/utils/comunas";
import { useSelector, useDispatch } from "react-redux";
import { useState, useEffect, useCallback, useRef } from "react";
import { importAlertState } from "src/redux/actions/alert";

export const ContactDetail = ({ variables, loading, loadingDelete, onChange, onSubmit, onDelete, contactId }) => {
  const { t } = useTranslation("agenda");
  const dispatch = useDispatch();
  const { selectedCompany: { id: companyId } } = useSelector((state) => state.companyList);
  const showLabelField = ['677d45356d129a001c2d0cbe'].includes(companyId);
  
  // Estado para manejar los contactos secundarios
  const [secondaryEmails, setSecondaryEmails] = useState([]);
  const debounceTimerRef = useRef(null);
  const [primaryEmailError, setPrimaryEmailError] = useState('');
  const [secondaryEmailErrors, setSecondaryEmailErrors] = useState({});
  const validationTimerRef = useRef(null);
  const secondaryValidationTimersRef = useRef({});

  // Función para validar formato de email completo
  const isValidEmailFormat = (email) => {
    if (!email || typeof email !== 'string') return false;
    const trimmedEmail = email.trim();
    if (trimmedEmail.length <= 1) return false;
    
    // Validar formato: texto + @ + dominio + extensión
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(trimmedEmail);
  };

  // Función para validar que el email principal sea válido
  const isValidPrimaryEmail = useCallback(() => {
    return isValidEmailFormat(variables?.email);
  }, [variables?.email]);

  // Inicializar contactos secundarios desde variables (solo al cargar)
  const isInitialMount = useRef(true);
  const isUserTypingRef = useRef(false);
  
  useEffect(() => {
    // En el primer render, cargar desde variables
    if (isInitialMount.current) {
      isInitialMount.current = false;
      if (variables?.secondaryEmails && Array.isArray(variables.secondaryEmails) && variables.secondaryEmails.length > 0) {
        setSecondaryEmails(variables.secondaryEmails.map((email, index) => ({
          id: `secondary-${Date.now()}-${index}`,
          email: email || ''
        })));
      }
      return;
    }
    
    // Después del primer render, solo actualizar si el usuario no está escribiendo
    // y las variables cambiaron desde fuera
    if (!isUserTypingRef.current) {
      if (variables?.secondaryEmails && Array.isArray(variables.secondaryEmails) && variables.secondaryEmails.length > 0) {
        const variableEmails = variables.secondaryEmails.filter(e => e && e.trim() !== '');
        const currentEmails = secondaryEmails.map(c => c.email).filter(e => e.trim() !== '');
        
        if (JSON.stringify(variableEmails.sort()) !== JSON.stringify(currentEmails.sort())) {
          setSecondaryEmails(variableEmails.map((email, index) => ({
            id: `secondary-${Date.now()}-${index}`,
            email: email || ''
          })));
        }
      } else if (!variables?.secondaryEmails && secondaryEmails.length > 0) {
        setSecondaryEmails([]);
      }
    }
  }, [variables?.secondaryEmails]);


  // Validar correo principal en tiempo real
  useEffect(() => {
    // Limpiar timer anterior
    if (validationTimerRef.current) {
      clearTimeout(validationTimerRef.current);
    }

    const email = variables?.email?.trim() || '';
    
    // Solo validar si hay texto ingresado
    if (email.length > 0) {
      validationTimerRef.current = setTimeout(() => {
        if (!isValidEmailFormat(email)) {
          setPrimaryEmailError(t("contactDetail.invalidEmail"));
        } else {
          setPrimaryEmailError('');
        }
      }, 500); // Validar después de 500ms sin escribir
    } else {
      setPrimaryEmailError('');
    }

    return () => {
      if (validationTimerRef.current) {
        clearTimeout(validationTimerRef.current);
      }
    };
  }, [variables?.email, t]);

  // Limpiar timers al desmontar
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (validationTimerRef.current) {
        clearTimeout(validationTimerRef.current);
      }
      // Limpiar todos los timers de validación de correos secundarios
      Object.values(secondaryValidationTimersRef.current).forEach(timer => {
        if (timer) clearTimeout(timer);
      });
    };
  }, []);
  // Función para agregar un contacto secundario
  const addSecondaryEmail = () => {
    if (secondaryEmails.length < 3) {
      const newId = `secondary-${Date.now()}`;
      setSecondaryEmails([...secondaryEmails, { id: newId, email: '' }]);
    }
  };

  // Función para validar todos los correos antes de guardar
  const validateBeforeSave = () => {
    const errors = [];
    
    // Obtener el correo principal directamente del input para asegurar que tenemos el valor actual
    const primaryEmailInput = document.getElementById('primary-email-input');
    const primaryEmail = primaryEmailInput ? primaryEmailInput.value.trim() : (variables?.email?.trim() || '');
    const hasSecondaryEmails = secondaryEmails.some(contact => contact.email && contact.email.trim() !== '');
    
    // Validar correo principal: si tiene algún valor ingresado, debe ser válido
    if (primaryEmail && primaryEmail.length > 0) {
      if (!isValidEmailFormat(primaryEmail)) {
        errors.push({
          field: 'primary',
          message: t("contactDetail.invalidPrimaryEmail"),
        });
      }
    } else if (hasSecondaryEmails) {
      errors.push({
        field: 'primary',
        message: t("contactDetail.primaryRequiredForSecondary"),
      });
    }
    
    secondaryEmails.forEach((contact) => {
      const email = contact.email?.trim() || '';
      if (email && email.length > 0 && !isValidEmailFormat(email)) {
        errors.push({
          field: 'secondary',
          id: contact.id,
          message: t("contactDetail.invalidSecondaryEmail", { email }),
        });
      }
    });
    
    return errors;
  };

  // Función para eliminar un contacto secundario
  const removeSecondaryEmail = (id) => {
    // Limpiar timer de debounce si existe
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    const updated = secondaryEmails.filter(contact => contact.id !== id);
    setSecondaryEmails(updated);
    // Actualizar variables inmediatamente
    const emails = updated.map(c => c.email).filter(e => e.trim() !== '');
    onChange("secondaryEmails", emails);
  };

  // Función para actualizar un contacto secundario con debounce para mejorar rendimiento
  const updateSecondaryEmail = (id, email) => {
    // Marcar que el usuario está escribiendo
    isUserTypingRef.current = true;
    const updated = secondaryEmails.map(contact =>
      contact.id === id ? { ...contact, email } : contact
    );
    setSecondaryEmails(updated);
    
    // Limpiar timer de debounce anterior
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    // Limpiar timer de validación anterior para este correo específico
    if (secondaryValidationTimersRef.current[id]) {
      clearTimeout(secondaryValidationTimersRef.current[id]);
    }
    
    // Validar correo secundario en tiempo real con debounce
    const trimmedEmail = email.trim();
    if (trimmedEmail.length > 0) {
      secondaryValidationTimersRef.current[id] = setTimeout(() => {
        if (!isValidEmailFormat(trimmedEmail)) {
          setSecondaryEmailErrors(prev => ({ ...prev, [id]: t("contactDetail.invalidEmail") }));
        } else {
          setSecondaryEmailErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[id];
            return newErrors;
          });
        }
        // Limpiar la referencia del timer después de ejecutarse
        delete secondaryValidationTimersRef.current[id];
      }, 500);
    } else {
      setSecondaryEmailErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[id];
        return newErrors;
      });
    }
    
    // Actualizar variables con debounce (solo después de 500ms sin escribir)
    debounceTimerRef.current = setTimeout(() => {
      const emails = updated.map(c => c.email).filter(e => e.trim() !== '');
      onChange("secondaryEmails", emails);
      // Marcar que el usuario terminó de escribir después de actualizar
      setTimeout(() => {
        isUserTypingRef.current = false;
      }, 100);
    }, 500);
  };

  return (
    <div className="parametrizacion-tab-content">
      <div className="parametrizacion-section">
        <span className="parametrizacion-section-title">{t("initialData")}</span>
        <div className="datos-iniciales-tabla">
          <span className="datos-iniciales-label">{t("rutLabel")}</span>
          <span className="dato-inicial-value">{formatRut(variables?.rut)}</span>
          <span className="datos-iniciales-label">{t("nameLabel")}</span>
          <span className="dato-inicial-value">{variables?.name}</span>
        </div>
      </div>

      <div className="parametrization-divider" />

      <div className="parametrizacion-section">
        <span className="parametrizacion-section-title">{t("contactDetail.contact")}</span>
        
        <div className="field">
          <label className="label">{t("contactDetail.primaryEmail")}</label>
          <input
            className="parametrization-input"
            placeholder={t("contactDetail.primaryEmailPlaceholder")}
            value={variables?.email || ""}
            onChange={(e) => onChange("email", e.target.value)}
            type="email"
            id="primary-email-input"
            style={{
              borderColor: primaryEmailError ? '#dc2626' : undefined
            }}
          />
          {primaryEmailError && (
            <div style={{
              color: '#dc2626',
              fontSize: '12px',
              marginTop: '4px',
              marginLeft: '4px'
            }}>
              {primaryEmailError}
            </div>
          )}
        </div>

        {/* Contactos Secundarios */}
        <div className="field" style={{ marginTop: 24 }}>
          <label className="label" style={{ marginBottom: 12 }}>
            {t("contactDetail.secondaryEmails")}
          </label>
          {secondaryEmails.map((contact, index) => (
            <div key={contact.id} style={{ 
              marginBottom: 12
            }}>
              <div style={{ 
                display: 'flex', 
                gap: 8, 
                alignItems: 'flex-start'
              }}>
                <div style={{ flex: 1 }}>
                  <input
                    className="parametrization-input"
                    placeholder={t("contactDetail.secondaryEmailPlaceholder", { index: index + 1 })}
                    value={contact.email || ""}
                    onChange={(e) => updateSecondaryEmail(contact.id, e.target.value)}
                    type="email"
                    style={{ 
                      width: '100%',
                      borderColor: secondaryEmailErrors[contact.id] ? '#dc2626' : undefined
                    }}
                    id={`secondary-email-${contact.id}`}
                  />
                  {secondaryEmailErrors[contact.id] && (
                    <div style={{
                      color: '#dc2626',
                      fontSize: '12px',
                      marginTop: '4px',
                      marginLeft: '4px'
                    }}>
                      {secondaryEmailErrors[contact.id]}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    removeSecondaryEmail(contact.id);
                    // Limpiar error al eliminar
                    setSecondaryEmailErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors[contact.id];
                      return newErrors;
                    });
                  }}
                  style={{
                    background: 'transparent',
                    border: '1px solid #dc2626',
                    color: '#dc2626',
                    borderRadius: 8,
                    padding: '8px 12px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: 40,
                    height: 40
                  }}
                  title={t("contactDetail.removeSecondaryContact")}
                >
                  <Icon name="minus" />
                </button>
              </div>
            </div>
          ))}
          {secondaryEmails.length < 3 && (
            <button
              type="button"
              onClick={addSecondaryEmail}
              disabled={!isValidPrimaryEmail()}
              style={{
                background: !isValidPrimaryEmail() ? '#f3f4f6' : 'transparent',
                border: '1px dashed #488e9d',
                color: !isValidPrimaryEmail() ? '#9ca3af' : '#488e9d',
                borderRadius: 8,
                padding: '8px 16px',
                cursor: !isValidPrimaryEmail() ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginTop: 8,
                width: '100%',
                justifyContent: 'center',
                opacity: !isValidPrimaryEmail() ? 0.6 : 1
              }}
              title={
                !isValidPrimaryEmail()
                  ? t("contactDetail.addSecondaryRequiresPrimary")
                  : t("contactDetail.addSecondaryContactTitle")
              }
            >
              <Icon name="plus" />
              {t("contactDetail.addSecondaryContact")}
            </button>
          )}
        </div>

        {showLabelField && (
          <div className="field" style={{ marginTop: 24 }}>
            <label className="label">{t("contactDetail.label")}</label>
            <input
              className="parametrization-input"
              placeholder={t("contactDetail.labelPlaceholder")}
              value={variables.labels?.length > 0 ? variables.labels[0] : ''}
              onChange={(e) => onChange("labels", [e.target.value])}
            />
          </div>
        )}
      </div>

      <div className="parametrization-divider" />

      <div className="parametrizacion-section">
        <span className="parametrizacion-section-title">{t("contactDetail.contactType")}</span>
        <div className="field-row" style={{ flexWrap: 'wrap', gap: 12 }}>
          <div
            className="field contact-type-option"
            style={{
              flex: '0 0 auto',
              minWidth: 91,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 16px',
              borderRadius: 8,
              backgroundColor: variables?.isClient ? 'rgba(72, 142, 157, 0.12)' : 'transparent',
            }}
          >
            <Checkbox
              className="custom-checkbox"
              checked={variables?.isClient}
              onChange={(_, data) => onChange("isClient", data.checked)}
            />
            <span className="contact-type-label">{t("client")}</span>
          </div>
          <div
            className="field contact-type-option"
            style={{
              flex: '0 0 auto',
              minWidth: 112,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 16px',
              borderRadius: 8,
              backgroundColor: variables?.isProvider ? 'rgba(72, 142, 157, 0.12)' : 'transparent',
            }}
          >
            <Checkbox
              className="custom-checkbox"
              checked={variables?.isProvider}
              onChange={(_, data) => onChange("isProvider", data.checked)}
            />
            <span className="contact-type-label">{t("provider")}</span>
          </div>
        </div>
      </div>

      {variables?.isClient && (
        <>
          <div className="parametrization-divider" />
          <div className="parametrizacion-section">
            <span className="parametrizacion-section-title">{t("contactDetail.taxData")}</span>
            <FormGroup className="field-row" style={{ marginBottom: 0 }}>
              <div className="field field--wide">
                <label className="label">{t("contactDetail.address")}</label>
                <input
                  className="parametrization-input"
                  type="text"
                  placeholder={t("contactDetail.addressPlaceholder")}
                  value={variables?.address || ""}
                  onChange={(e) => onChange("address", e.target.value)}
                />
              </div>
              <div className="field field--wide">
                <label className="label">{t("contactDetail.commune")}</label>
                <div className="dropdown-wrapper">
                  <ComunasListSemantic
                    value={variables.zone}
                    onChange={(_, v) => onChange("zone", v.value)}
                  />
                </div>
              </div>
            </FormGroup>
            <div className="field">
              <label className="label">{t("contactDetail.activity")}</label>
              <input
                className="parametrization-input"
                type="text"
                placeholder={t("contactDetail.activityPlaceholder")}
                value={variables?.activity || ""}
                onChange={(e) => onChange("activity", e.target.value)}
              />
            </div>
          </div>
        </>
      )}

      <div className="parametrization-buttons">
        <button
          type="button"
          className="parametrization-btn parametrization-btn--primary"
          disabled={loading}
          onClick={(e) => {
            e.preventDefault();
            
            // Validar antes de guardar
            const validationErrors = validateBeforeSave();
            
            if (validationErrors.length > 0) {
              // Mostrar errores en los campos correspondientes
              validationErrors.forEach(error => {
                if (error.field === 'primary') {
                  setPrimaryEmailError(error.message);
                } else if (error.field === 'secondary') {
                  setSecondaryEmailErrors(prev => ({ ...prev, [error.id]: error.message }));
                }
              });
              
              // Hacer scroll al primer campo con error
              setTimeout(() => {
                if (validationErrors[0].field === 'primary') {
                  const primaryInput = document.getElementById('primary-email-input');
                  if (primaryInput) {
                    primaryInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    primaryInput.focus();
                  }
                } else if (validationErrors[0].field === 'secondary') {
                  const secondaryInput = document.getElementById(`secondary-email-${validationErrors[0].id}`);
                  if (secondaryInput) {
                    secondaryInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    secondaryInput.focus();
                  }
                }
              }, 100);
              
              // Mostrar alerta del Portal con el primer error
              dispatch(
                importAlertState({
                  content: validationErrors[0].message,
                  state: "error",
                  duration: 5,
                })
              );
              return;
            }
            
            // Limpiar errores si todo está válido
            setPrimaryEmailError('');
            setSecondaryEmailErrors({});
            
            // Guardar emails secundarios antes de enviar
            const emails = secondaryEmails.map(c => c.email).filter(e => e.trim() !== '');
            onChange("secondaryEmails", emails);
            
            onSubmit();
          }}
        >
          {t("saveChanges")}
        </button>
        <button type="button" className="parametrization-btn parametrization-btn--secondary">
          {t("parametrization.cancelChanges")}
        </button>
      </div>

      <div className="parametrization-divider" />

      <div className="parametrizacion-section">
        <span className="parametrizacion-section-title">{t("contactDetail.deleteContact")}</span>
        <div
          className="tw-rounded-lg tw-p-5 tw-flex tw-gap-3"
          style={{ backgroundColor: '#f9fafb', minHeight: 119 }}
        >
          <FeatherIcon className="tw-text-gray-500" icon="info" size={19.5} />
          <div>
            <span>{t("contactDetail.deleteIntro")}</span>
            <List as="ul">
              <ListItem as="li">
                {t("contactDetail.deletePermanent")}
              </ListItem>
              <ListItem as="li">
                {t("contactDetail.deleteNomina")}
              </ListItem>
            </List>
          </div>
        </div>
        <button
          type="button"
          className="parametrization-btn"
          style={{
            marginTop: 12,
            background: '#fff',
            color: '#dc2626',
            borderColor: '#dc2626',
          }}
          disabled={loadingDelete}
          onClick={(e) => {
            e.preventDefault();
            onDelete(contactId);
          }}
        >
          {loadingDelete ? t("contactDetail.deleting") : t("contactDetail.deleteContactBtn")}
        </button>
      </div>
    </div>
  );
};
